// src/hypotheken/hypotheken-berekening/services/accordion-state/accordion-state.service.ts

import { AccordionItem, AccordionItemState, AccordionStep } from '@hb/domain';
import * as content from '@hb/hypotheken-berekening.content.json';
import { StateService } from '@hb/services/state/state.service';

import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable } from 'rxjs';

import { ApContentService } from '@ap/content';

import { ACCORDION_STEPS_CONFIG } from './accordion-config.token';

@Injectable({
    providedIn: 'root'
})
export class AccordionStateService {
    private readonly stateService = inject(StateService);
    private readonly contentService = inject(ApContentService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly stepsConfig = inject(ACCORDION_STEPS_CONFIG);

    private accordionStateSubject!: BehaviorSubject<AccordionItem[]>;
    private readonly isOpenAccordionStepSubject = new BehaviorSubject<AccordionStep>(AccordionStep.INCOME);
    private readonly displayFirstStepOnly = new BehaviorSubject<boolean>(true);

    private isInitialized = false;

    init(): void {
        if (this.isInitialized) {
            return;
        }
        this.isInitialized = true;

        this.contentService.addContentItems(content);
        const accordion = this.buildInitialAccordion();
        this.setInitialAccordionState(accordion);
        this.subscribeAccordionState();
    }

    private buildInitialAccordion(): AccordionItem[] {
        return this.stepsConfig.map((cfg, index) => ({
            step: cfg.step,
            title: this.contentService.get(cfg.titleKey),
            component: cfg.component,
            icon: cfg.icon,
            isFinished: false,
            isOpen: index === 0,
            isVisible: true,
            isDisabled: index !== 0
        }));
    }

    private subscribeAccordionState(): void {
        this.getAccordionState()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((state) => {
                this.stateService.setAccordionState(
                    state.map(
                        (item: AccordionItem): AccordionItemState => ({
                            step: item.step,
                            isFinished: item.isFinished,
                            isOpen: item.isOpen,
                            isVisible: item.isVisible,
                            isDisabled: item.isDisabled
                        })
                    )
                );
                this.setOpenAccordionStep(state);
                this.setDisplayFirstStepOnly(state);
            });
    }

    private setInitialAccordionState(accordion: AccordionItem[]): void {
        const accordionState = this.stateService.getAccordionState();
        if (accordionState) {
            this.accordionStateSubject = new BehaviorSubject<AccordionItem[]>(
                accordionState.map((state) => ({
                    ...accordion.find((item) => item.step === state.step),
                    ...state
                }))
            );
        } else {
            this.accordionStateSubject = new BehaviorSubject<AccordionItem[]>(accordion);
        }
    }

    private setDisplayFirstStepOnly(state: AccordionItem[]): void {
        const biggestEnabledItem = Math.max(...state.filter((item) => !item.isDisabled).map((item) => item.step));
        if (biggestEnabledItem > AccordionStep.INCOME) {
            this.displayFirstStepOnly.next(false);
        }
    }

    private setOpenAccordionStep(state: AccordionItem[]): void {
        const isOpenAccordionItem = state.find((item) => item.isOpen);
        if (isOpenAccordionItem) {
            this.isOpenAccordionStepSubject.next(isOpenAccordionItem.step);
        }
    }

    getAccordionState(): Observable<AccordionItem[]> {
        return this.accordionStateSubject.asObservable();
    }

    nextAccordionStep(): void {
        const accordionState = this.accordionStateSubject.value;
        const currentOpenAccordionStep = this.getOpenAccordionStep();
        const accordionStepsToDo = accordionState
            .filter((item) => !item.isOpen)
            .filter((item) => !item.isFinished)
            .filter((item) => item.isVisible);
        const nextAccordionStep = Math.min(...accordionStepsToDo.map((item) => item.step));
        this.setStepFinished(currentOpenAccordionStep.step);
        this.setAccordionStepOpen(nextAccordionStep);
    }

    stepBack(): void {
        const currentOpenAccordionStep = this.getOpenAccordionStep();
        const nextOpenAccordionStep = this.accordionStateSubject.value
            .filter((item) => item.isVisible)
            .find((item) => item.step === currentOpenAccordionStep.step - 1);

        this.setAccordionStepOpen(nextOpenAccordionStep!.step);
    }

    getStepIsVisible(step: AccordionStep): boolean {
        return this.accordionStateSubject.value.find((item) => item.step === step)!.isVisible;
    }

    getStepIsFinished(accordionStep: AccordionStep): boolean {
        return this.accordionStateSubject.value.find((stepState) => stepState.step === accordionStep)!.isFinished;
    }

    getStepIsOpen(accordionStep: AccordionStep): boolean {
        return this.accordionStateSubject.value.find((stepState) => stepState.step === accordionStep)!.isOpen;
    }

    setStepNotFinished(step: AccordionStep): void {
        const accordionState = this.accordionStateSubject.value;
        accordionState[step].isFinished = false;
        accordionState.forEach((item) => {
            if (item.step > step) {
                item.isDisabled = true;
            }
        });
        this.accordionStateSubject.next(accordionState);
    }

    private setStepFinished(step: AccordionStep): void {
        const accordionState = this.accordionStateSubject.value;
        accordionState[step].isFinished = true;
        accordionState[step + 1].isDisabled = false;
        accordionState.forEach((item) => {
            if ((item.step > step && item.isFinished) || item.step === this.getLatestAccordionStep()) {
                item.isDisabled = false;
            }
        });
        this.accordionStateSubject.next(accordionState);
    }

    getOpenAccordionStepObservable(): Observable<AccordionStep> {
        return this.isOpenAccordionStepSubject.asObservable();
    }

    getOpenAccordionStep(): AccordionItem {
        const accordionState = this.accordionStateSubject.value;
        return accordionState.find((item) => item.isOpen)!;
    }

    setManualOpened(accordionStep: AccordionStep): void {
        this.setAccordionStepOpen(accordionStep);
    }

    private setAccordionStepOpen(step: AccordionStep): void {
        const accordionState = this.accordionStateSubject.value;
        accordionState.forEach((item) => (item.isOpen = false));
        accordionState.find((item) => item.step === step)!.isOpen = true;
        this.accordionStateSubject.next(accordionState);
    }

    getLatestAccordionStep(): AccordionStep {
        const accordionStepsDone = this.accordionStateSubject.value.filter((item) => item.isFinished).map((item) => item.step);
        if (accordionStepsDone.length > 0) {
            return Math.max(...accordionStepsDone) + 1;
        }
        return AccordionStep.INCOME;
    }

    setStepNotVisible(step: AccordionStep): void {
        const accordionState = this.accordionStateSubject.value;
        const index = accordionState.findIndex((item) => item.step === step);
        accordionState[index].isVisible = false;
        accordionState[index].isFinished = true;
        this.accordionStateSubject.next(accordionState);
    }

    setStepVisible(step: AccordionStep): void {
        const accordionState = this.accordionStateSubject.value;
        const accordionStep = accordionState.find((item) => item.step === step)!;
        if (!accordionStep.isVisible) {
            accordionStep.isVisible = true;
            accordionStep.isFinished = false;
            this.accordionStateSubject.next(accordionState);
        }
    }

    setStepEnabled(step: AccordionStep): void {
        const accordionState = this.accordionStateSubject.value;
        const accordionStep = accordionState.find((item) => item.step === step)!;
        accordionStep.isDisabled = false;
        this.accordionStateSubject.next(accordionState);
    }

    getDisplayFirstStepOnly(): boolean {
        return this.displayFirstStepOnly.value;
    }

    setDisplayFullAccordion(): void {
        this.displayFirstStepOnly.next(false);
    }
}
